import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import LandingPage from './components/LandingPage';
import NxPage from './components/nx/NxPage';
import { impactHtml, aboutHtml, whatWeDoHtml } from './components/nx/pages';
import { privacyHtml } from './components/nx/privacy';
import PartnerLogos from './components/nx/PartnerLogos';
import CommunityBlogs from './components/CommunityBlogs';
import OurImpact from './components/OurImpact';
import Shop from './components/Shop';
import AIConsulting from './components/AIConsulting';
import Members from './components/Members';
import MbuziPlusParent from './components/MbuziPlusParent';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieNotice, { CookieChoice } from './components/CookieNotice';
import ServicesPage from './components/ServicesPage';
import AnnouncementBar, { ANNOUNCEMENT_HEIGHT } from './components/AnnouncementBar';
import AudiencePage from './components/AudiencePage';
import Contact from './components/landingcomps/Contact';
import { PAGE_META } from './data/pageMeta';
import Loading from './components/Loading';
import ImfuyoAcademy from './components/ImfuyoAcademy';
import ProductPage from './components/ProductPage';
import MyAccount from './components/MyAccount';

// ── Firebase Analytics ──────────────────────────────────────────────────────
import {
  analytics,
  trackPageView,
  trackWhatsAppClick,
  trackMbuziPlusInterest,
} from './firebase';
// ───────────────────────────────────────────────────────────────────────────

const ABOUT_SLOTS = { partners: <PartnerLogos mainOnly /> };

const PAGE_TITLES = {
  landing:      'Home — Imfuyo',
  about:        'About Us — Imfuyo',
  community:    'Community & Blogs — Imfuyo',
  whatwedo:     'What We Do — Imfuyo',
  impact:       'Our Impact — Imfuyo',
  shop:         'Shop — Imfuyo',
  aiconsulting: 'AI Consulting — Imfuyo',
  members:      'Members — Imfuyo',
  mbuziplus:    'MbuziPlus — Imfuyo',
  academy:      'Imfuyo Academy — Imfuyo',
  privacy:      'Privacy and Cookies — Imfuyo',
  services:     'Apply Here — Imfuyo Services',
  insurance:    'Livestock Insurance — Imfuyo Finance',
  loans:        'Farmer Loans — Imfuyo Finance',
  farmers:      'For Farmers — Imfuyo',
  groups:       'For Farmer Groups — Imfuyo',
  cooperatives: 'For Cooperatives — Imfuyo',
  insurers:     'For Insurance Companies — Imfuyo',
  lenders:      'For Lenders — Imfuyo',
  vets:         'For Veterinary Offices — Imfuyo',
  buyers:       'For Buyers and Processors — Imfuyo',
  governments:  'For Governments and Universities — Imfuyo',
  contact:      'Contact — Imfuyo',
  account:      'My Imfuyo account — Imfuyo',
};

// Floating WhatsApp Button Component
const FloatingWhatsAppButton = ({ isDark, onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasShownInitial, setHasShownInitial] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTooltip(true);
      setHasShownInitial(true);
      setTimeout(() => { setShowTooltip(false); }, 5000);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed bottom-6 right-4 sm:right-6 z-50 pointer-events-auto">
      <div
        className={`absolute bottom-20 right-0 transition-all duration-500 transform ${
          showTooltip ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
        }`}
        style={{ maxWidth: '280px', minWidth: '240px' }}
      >
        <div
          className={`hidden sm:block relative rounded-2xl p-4 shadow-2xl ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
          style={{ animation: showTooltip ? 'bounce-in 0.5s ease-out' : 'none' }}
        >
          <button
            onClick={() => setShowTooltip(false)}
            className={`absolute -top-2 -right-2 p-1.5 rounded-full transition-all duration-200 ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            <X className="w-3 h-3" />
          </button>
          <p
            className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            💬 Reach out to us on WhatsApp!
          </p>
          <div
            className={`absolute -bottom-2 right-8 w-4 h-4 transform rotate-45 ${
              isDark ? 'bg-gray-800 border-r border-b border-gray-700' : 'bg-white border-r border-b border-gray-200'
            }`}
          />
        </div>
      </div>

      <button
        onClick={onClick}
        onMouseEnter={() => hasShownInitial && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="group relative w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
        style={{ boxShadow: '0 8px 32px rgba(34, 197, 94, 0.4)' }}
      >
        <div className="absolute inset-0 rounded-full animate-ping opacity-75 bg-green-400" style={{ animationDuration: '2s' }} />
        <svg className="w-7 h-7 sm:w-8 sm:h-8 text-white relative z-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center border-2 border-white">
          <span className="text-white text-xs font-bold">1</span>
        </div>
      </button>
    </div>
  );
};

// Meta tags updater for SEO
const updateMetaTags = (page) => {
  const metaInfo = {
    landing: {
      title: 'Imfuyo | Data & Finance Company',
      description: 'Imfuyo is a data and finance company in the livestock value chain. We run a data and financial infrastructure platform and co-create products with farmers, farmer groups, cooperatives, insurance companies and lenders.',
      keywords: 'Imfuyo, livestock data, livestock finance, livestock insurance, MbuziPlus, dairy goats, Kenya',
    },
    privacy: {
      title: 'Privacy and Cookies | Imfuyo',
      description: 'What Imfuyo collects, why, who sees it, and how you can control your information and cookie choice.',
      keywords: 'Imfuyo privacy, cookies, data protection Kenya',
    },
    about: {
      title: 'About Imfuyo | Data & Finance Company',
      description: "Imfuyo Usajili Solutions Limited is a data and finance company in the livestock value chain. Imfuyo is a Zulu word meaning livestock and wealth.",
      keywords: 'about Imfuyo, farmer support, agricultural innovation, livestock identification',
    },
    community: {
      title: 'Community & Blogs - Imfuyo | Farmer Stories and Insights',
      description: 'Read inspiring stories from African farmers, access educational resources, and join our community of agricultural innovators.',
      keywords: 'farmer stories, agricultural blog, farming community, farmer resources',
    },
    whatwedo: {
      title: 'What We Do | Imfuyo',
      description: "How Imfuyo helps make livestock visible, protected and financeable with farmers, cooperatives, insurers and lenders.",
      keywords: 'farmer training, livestock services, agricultural finance, market access',
    },
    impact: {
      title: 'Impact | Imfuyo and Imfuyo Foundation',
      description: 'What Imfuyo and Imfuyo Foundation have achieved with farmers so far, and what we are working towards.',
      keywords: 'livestock impact, dairy goat farmers, Imfuyo Foundation, rural development, Kenya',
    },
    shop: {
      title: 'Shop - Imfuyo | Agricultural Products & Services',
      description: 'Access agricultural products, connect buyers with farmers, and discover quality livestock and produce.',
      keywords: 'agricultural marketplace, livestock sales, farm products, farmer marketplace',
    },
    aiconsulting: {
      title: 'AI Consulting - Imfuyo | Agricultural Technology Solutions',
      description: 'Leverage AI and technology to optimize farming operations. Get expert consulting for precision agriculture.',
      keywords: 'agricultural AI, precision farming, farm technology consulting, smart agriculture',
    },
    members: {
      title: 'Members - Imfuyo | Join Our Farmer Network',
      description: 'Become an Imfuyo member and access exclusive benefits, training, financial services, and a network of thousands of farmers.',
      keywords: 'farmer membership, agricultural network, farmer benefits, farming community',
    },
    mbuziplus: {
      title: 'MbuziPlus | Imfuyo',
      description: "MbuziPlus is Imfuyo's way of solving livestock value chain challenges for dairy goat farming in Kenya and Africa.",
      keywords: 'goat farming Kenya, dairy goat training, mbuziplus, goat economics, imfuyo training',
    },
    academy: {
      title: 'Imfuyo Academy | The MbuziPlus Course for Dairy Goat Farmers',
      description: 'The MbuziPlus Course by Imfuyo Academy: 8 parts and 42 modules on dairy goat farming, records, insurance and finance. Free 20-minute preview.',
      keywords: 'imfuyo academy, mbuziplus course, dairy goat course, livestock finance training, online farming course Kenya',
    },
    services: {
      title: 'Apply Here | Imfuyo Services',
      description: 'Every Imfuyo service in one place: livestock insurance, farmer loans, the MbuziPlus Course, farm visits, group registration and partner services. Find it and apply.',
      keywords: 'imfuyo services, livestock insurance, farmer loans, mbuziplus course, farmer group registration, apply',
    },
    insurance: {
      title: 'Livestock Insurance - Imfuyo Finance | Apply & Claim',
      description: 'Imfuyo Insurance Platform: apply for livestock cover and file claims for goats, pigs, cows and camels — backed by verified farmer and animal records.',
      keywords: 'livestock insurance Kenya, goat insurance, cattle insurance, livestock claims, imfuyo finance, agricultural insurance',
    },
    loans: {
      title: 'Farmer Loans - Imfuyo Finance | Get Matched to a Lender',
      description: "Imfuyo Farmer Loans turns your livestock enterprise's production data into a credit file, matched across our lender network — we never lend directly.",
      keywords: 'farmer loans Kenya, livestock credit score, agricultural lending, dairy farmer loans, imfuyo finance, livestock collateral, agri-lender matching',
    },
  };

  const shared = PAGE_META[page];
  const info = shared ? { ...(metaInfo[page] || metaInfo.landing), title: shared.title, description: shared.description } : (metaInfo[page] || metaInfo.landing);
  document.title = info.title;

  const setMeta = (sel, attr, val) => {
    let el = document.querySelector(sel);
    if (!el) { el = document.createElement('meta'); if (attr.includes(':')) el.setAttribute(attr.split(':')[0] + ':' + attr.split(':')[1], ''); else el.name = attr; document.head.appendChild(el); }
    el.content = val;
  };

  setMeta('meta[name="description"]', 'name', info.description);
  setMeta('meta[name="keywords"]', 'name', info.keywords);
  setMeta('meta[property="og:title"]', 'property', info.title);
  setMeta('meta[property="og:description"]', 'property', info.description);

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
  const pathFor = PAGE_META[page] ? PAGE_META[page].path : '/';
  canonical.href = `https://www.imfuyo.farm${pathFor === '/' ? '' : pathFor}`;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [lottieLoaded, setLottieLoaded] = useState(false);

  const getInitialPage = () => {
    const path = window.location.pathname.toLowerCase();
    const pathMap = {
      '/': 'landing',
      '/about': 'about',
      '/community': 'community',
      '/blogs': 'community',
      '/whatwedo': 'whatwedo',
      '/what-we-do': 'whatwedo',
      '/impact': 'impact',
      '/our-impact': 'impact',
      '/marketplace': 'shop',
      '/shop': 'shop',
      '/aiconsulting': 'aiconsulting',
      '/ai-consulting': 'aiconsulting',
      '/members': 'members',
      '/mbuziplus': 'mbuziplus',
      '/mbuzi-plus': 'mbuziplus',
      '/academy': 'academy',
      '/imfuyo-academy': 'academy',
      '/insurance': 'insurance',
      '/livestock-insurance': 'insurance',
      '/finance/insurance': 'insurance',
      '/loans': 'loans',
      '/farmer-loans': 'loans',
      '/finance/loans': 'loans',
      '/farmers': 'farmers', '/farmer-groups': 'groups', '/cooperatives': 'cooperatives',
      '/insurers': 'insurers', '/lenders': 'lenders', '/veterinary-offices': 'vets', '/buyers': 'buyers', '/governments': 'governments',
      '/privacy': 'privacy', '/privacy-policy': 'privacy', '/cookies': 'privacy', '/services': 'services', '/all-services': 'services', '/apply': 'services', '/contact': 'contact', '/contact-us': 'contact', '/my-account': 'account', '/account': 'account', '/blog': 'community', '/farmer-group': 'groups', '/vets': 'vets',
    };
    for (const [route, page] of Object.entries(pathMap)) {
      if (path === route || path === route + '/') return page;
    }
    // Deep shop links (/shop/livestock/freshiancow, /shop/products/...) —
    // not an exact match above, but still the Shop page. Without this, a
    // human landing on a deep link would fall through to the sessionStorage
    // check below and likely land on 'landing' instead of Shop.
    if (path.startsWith('/shop/')) return 'shop';
    return sessionStorage.getItem('currentPage') || 'landing';
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [isDark, setIsDark] = useState(() => sessionStorage.getItem('isDark') === 'true');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [targetSection, setTargetSection] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  // Announcement bar: sits above the menu until closed
  // The visitor's choice to close it is remembered for seven days; it also stays away once the free lesson has been started.
  const [annOpen, setAnnOpen] = useState(() => {
    try {
      const until = parseInt(localStorage.getItem('annClosedUntil') || '0', 10);
      if (until > Date.now()) return false;
      if (localStorage.getItem('imfuyo-academy-teaser-v1')) return false;
      return true;
    } catch (e) { return true; }
  });
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(Math.min(window.scrollY, ANNOUNCEMENT_HEIGHT));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── ANALYTICS: Fire page_view on every page change ──────────────────────
  useEffect(() => {
    trackPageView(currentPage, PAGE_TITLES[currentPage]);
  }, [currentPage]);
  // ─────────────────────────────────────────────────────────────────────────

  // URL + meta sync
  useEffect(() => {
    const pageToPath = {
      landing: '/',
      about: '/about',
      community: '/community',
      whatwedo: '/whatwedo',
      impact: '/impact',
      shop: '/shop',
      aiconsulting: '/aiconsulting',
      members: '/members',
      mbuziplus: '/mbuziplus',
      academy: '/academy',
      insurance: '/insurance',
      loans: '/loans',
      farmers: '/farmers', groups: '/farmer-groups', cooperatives: '/cooperatives',
      insurers: '/insurers', lenders: '/lenders', vets: '/veterinary-offices', buyers: '/buyers', governments: '/governments',
      contact: '/contact', account: '/my-account', privacy: '/privacy', services: '/apply',
    };
    const newPath = pageToPath[currentPage] || '/';
    // Don't stomp a deep shop link (/shop/livestock/freshiancow) back down
    // to plain /shop on the very next render — only push the mapped path
    // when the browser isn't already sitting on a more specific /shop/...
    // URL than the one we'd otherwise force it to.
    const onDeeperShopPath = currentPage === 'shop' && window.location.pathname.toLowerCase().startsWith('/shop/');
    if (window.location.pathname !== newPath && !onDeeperShopPath) {
      window.history.pushState({ page: currentPage }, '', newPath);
    }
    updateMetaTags(currentPage);
    sessionStorage.setItem('currentPage', currentPage);
  }, [currentPage]);

  // Browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase();
      const pathMap = {
        '/': 'landing', '/about': 'about', '/community': 'community', '/blogs': 'community',
        '/whatwedo': 'whatwedo', '/what-we-do': 'whatwedo', '/impact': 'impact',
        '/our-impact': 'impact', '/marketplace': 'shop', '/shop': 'shop',
        '/aiconsulting': 'aiconsulting', '/ai-consulting': 'aiconsulting',
        '/members': 'members', '/mbuziplus': 'mbuziplus', '/mbuzi-plus': 'mbuziplus',
        '/academy': 'academy', '/imfuyo-academy': 'academy',
        '/insurance': 'insurance', '/livestock-insurance': 'insurance',
        '/loans': 'loans', '/farmer-loans': 'loans',
        '/farmers': 'farmers', '/farmer-groups': 'groups', '/cooperatives': 'cooperatives',
        '/insurers': 'insurers', '/lenders': 'lenders', '/veterinary-offices': 'vets', '/buyers': 'buyers', '/governments': 'governments',
        '/privacy': 'privacy', '/privacy-policy': 'privacy', '/cookies': 'privacy', '/services': 'services', '/all-services': 'services', '/apply': 'services', '/contact': 'contact', '/contact-us': 'contact', '/my-account': 'account', '/account': 'account', '/blog': 'community', '/farmer-group': 'groups', '/vets': 'vets',
      };
      // Same prefix-matching fix as getInitialPage, so navigating back/forward
      // between a deep shop link and the plain /shop page (or any other page)
      // resolves to 'shop' instead of falling back to 'landing'.
      const newPage = pathMap[path] || pathMap[path.replace(/\/$/, '')] || (path.startsWith('/shop/') ? 'shop' : 'landing');
      if (newPage !== currentPage) { setCurrentPage(newPage); window.scrollTo(0, 0); setActiveSection(0); }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentPage]);

  useEffect(() => { sessionStorage.setItem('isDark', isDark); }, [isDark]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      try {
        await new Promise(resolve => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', 'https://lottie.host/68638530-e1e4-49f9-952a-1c3efcd7aeac/2hywu50vnf.lottie');
          xhr.onload = () => resolve(true);
          xhr.onerror = () => resolve(true);
          xhr.send();
        });
        setLottieLoaded(true);
        setTimeout(() => setIsLoading(false), 2000);
      } catch {
        setTimeout(() => setIsLoading(false), 2000);
      }
    };
    load();
  }, []);

  const handleNavigation = (page, section = null) => {
    if (page === 'landing' && section === 'contact') { page = 'contact'; section = null; }
    if (mobileMenuOpen) setMobileMenuOpen(false);
    if (page === currentPage && section) { setTargetSection(section); return; }
    if (page !== currentPage) {
      setIsTransitioning(true);
      setLottieLoaded(false);
      setTimeout(() => setLottieLoaded(true), 500);
      setTimeout(() => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
        setActiveSection(0);
        if (section) setTimeout(() => setTargetSection(section), 300);
        setIsTransitioning(false);
      }, 2000);
    }
  };

  const navigateToAbout        = () => handleNavigation('about');
  const navigateToHome         = () => handleNavigation('landing');
  const navigateToCommunity    = () => handleNavigation('community');
  const navigateToAIConsulting = () => handleNavigation('aiconsulting');
  const navigateToMembers      = () => handleNavigation('members');
  const navigateToAcademy      = () => handleNavigation('academy');

  // ── ANALYTICS: track WhatsApp clicks ────────────────────────────────────
  const handleWhatsAppClick = () => {
    trackWhatsAppClick();
    window.open('https://wa.me/254110563185?text=Hello%20Imfuyo!%20I%20have%20a%20question', '_blank');
  };
  // ─────────────────────────────────────────────────────────────────────────

  const isHero = currentPage === 'landing' && activeSection === 0;

  if (isLoading || isTransitioning) return <Loading lottieLoaded={lottieLoaded} />;

  // Pages that manage their own compact footer / chrome and should not get
  // the full-size global Footer, so their content stays the focus.
  const HIDE_GLOBAL_FOOTER = ['aiconsulting', 'shop', 'insurance', 'loans'];

  return (
    <div className="App">
      {annOpen && (
        <AnnouncementBar
          onOpen={() => handleNavigation('academy')}
          onClose={() => { setAnnOpen(false); try { localStorage.setItem('annClosedUntil', String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch (e) {} }}
        />
      )}
      <div className="fixed left-0 right-0 z-50" style={{ top: annOpen ? Math.max(0, ANNOUNCEMENT_HEIGHT - scrollY) : 0 }}>
        <Navbar
          currentPage={currentPage}
          onNavigate={handleNavigation}
          isDark={isDark}
          setIsDark={setIsDark}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          isHero={isHero}
          activeSection={activeSection}
        />
      </div>

      <div className="min-h-screen">
        {currentPage === 'landing' && (
          <LandingPage
            isDark={isDark} setIsDark={setIsDark}
            onNavigateToAbout={navigateToAbout}
            onNavigateToCommunity={navigateToCommunity}
            onNavigateToAIConsulting={navigateToAIConsulting}
            onNavigate={handleNavigation}
            targetSection={targetSection} setTargetSection={setTargetSection}
            activeSection={activeSection} setActiveSection={setActiveSection}
          />
        )}
        {currentPage === 'about' && <NxPage html={aboutHtml} slots={ABOUT_SLOTS} onNavigate={handleNavigation} />}
        {currentPage === 'community'    && <CommunityBlogs isDark={isDark} onBack={navigateToHome} onNavigate={handleNavigation} />}
        {currentPage === 'whatwedo' && <NxPage html={whatWeDoHtml} onNavigate={handleNavigation} />}
        {currentPage === 'impact' && <NxPage html={impactHtml} onNavigate={handleNavigation} />}
        {currentPage === 'privacy' && <NxPage html={privacyHtml} slots={{ cookiechoice: <CookieChoice /> }} onNavigate={handleNavigation} />}
        {currentPage === 'services' && <ServicesPage isDark={isDark} onNavigate={handleNavigation} />}
        {currentPage === 'contact' && <div style={{ paddingTop: '72px' }}><Contact isDark={isDark} /></div>}
        {currentPage === 'account' && <MyAccount isDark={isDark} />}
        {currentPage === 'shop'         && <Shop           isDark={isDark} onBack={navigateToHome} onNavigate={handleNavigation} />}
        {currentPage === 'aiconsulting' && <AIConsulting   isDark={isDark} onBack={navigateToHome} onNavigate={handleNavigation} />}
        {currentPage === 'members'      && <Members        isDark={isDark} onBack={navigateToHome} />}
        {currentPage === 'mbuziplus'    && <MbuziPlusParent isDark={isDark} onBack={navigateToHome} onNavigate={handleNavigation} />}
        {currentPage === 'academy'      && <ImfuyoAcademy  isDark={isDark} onBack={navigateToHome} onNavigate={handleNavigation} />}
        {currentPage === 'insurance'    && <ProductPage product="insurance" isDark={isDark} onNavigate={handleNavigation} />}
        {currentPage === 'loans'        && <ProductPage product="loans" isDark={isDark} onNavigate={handleNavigation} />}
        {['farmers','groups','cooperatives','insurers','lenders','vets','buyers','governments'].includes(currentPage) && (
          <AudiencePage audience={currentPage} isDark={isDark} onNavigate={handleNavigation} />
        )}
      </div>


      {!HIDE_GLOBAL_FOOTER.includes(currentPage) && <Footer onNavigate={handleNavigation} />}

      <FloatingWhatsAppButton isDark={isDark} onClick={handleWhatsAppClick} />
      <CookieNotice />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
        @keyframes bounce-in { 0%{opacity:0;transform:scale(0.8) translateY(20px)} 50%{transform:scale(1.05) translateY(-5px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes ping { 75%,100%{transform:scale(2);opacity:0} }
        .animate-ping { animation: ping 1s cubic-bezier(0,0,0.2,1) infinite; }
      `}</style>
    </div>
  );
}

export default App;